import { get, ref } from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import { Character } from "../../domain/models/Character";
import { CharacterStory } from "../../domain/models/CharacterStory";
import { CoreError, CoreErrorCause } from "../../domain/models/CoreError";
import { CharacterConverter } from "./converters/CharacterConverter";
import { CharacterStoryConverter } from "./converters/CharacterStoryConverter";

const STORIES_LANGUAGE = "EN";

/**
 * Read-only access to the fictional characters catalog.
 *
 * `characters/<id>` holds the light profile/persona data (listed by the app);
 * `characterCards/<id>/liked|disliked` holds the precomputed swipe sets and is
 * fetched only for the character being played. Both nodes are static content
 * written by the import pipeline — never observed, cached in memory.
 */
export class CharacterService {
  private static instance: CharacterService;
  private cachedCharacters: Character[] | null = null;
  private fetchPromise: Promise<Character[]> | null = null;
  private cachedStoriesByPair = new Map<string, CharacterStory | null>();
  private storyCardIdsPromises = new Map<string, Promise<Set<string>>>();

  private constructor() {}

  public static getInstance(): CharacterService {
    if (!CharacterService.instance) {
      CharacterService.instance = new CharacterService();
    }
    return CharacterService.instance;
  }

  /**
   * Wraps raw Firebase errors in CoreError, whose constructor logs to console
   * and Sentry — callers keep their best-effort fallbacks, but a failed read
   * (e.g. permission denied because the rules were not redeployed) is never
   * silent again.
   */
  private toCoreError(error: unknown, context: string): CoreError {
    if (error instanceof CoreError) {
      return error;
    }
    return new CoreError({
      cause: CoreErrorCause.Unknown,
      context,
      underlyingException: error,
    });
  }

  public async fetchCharacters(
    forceRefresh: boolean = false,
  ): Promise<Character[]> {
    if (this.cachedCharacters && !forceRefresh) {
      return this.cachedCharacters;
    }

    if (this.fetchPromise && !forceRefresh) {
      return this.fetchPromise;
    }

    this.fetchPromise = this.performFetch();

    try {
      const characters = await this.fetchPromise;
      this.cachedCharacters = characters;
      return characters;
    } catch (error) {
      this.fetchPromise = null;
      throw error;
    }
  }

  private async performFetch(): Promise<Character[]> {
    const snapshot = await get(ref(database, "characters")).catch((error) => {
      throw this.toCoreError(error, "Failed to read /characters");
    });
    if (!snapshot.exists()) {
      return [];
    }

    const data = snapshot.val();
    if (!data || typeof data !== "object") {
      return [];
    }

    const characters: Character[] = [];
    Object.entries(data).forEach(([identifier, characterData]) => {
      const character = CharacterConverter.fromFirebase(
        characterData as Record<string, unknown>,
        identifier,
      );
      if (character && character.isActive) {
        characters.push(character);
      }
    });

    return characters;
  }

  public async loadCharacter(characterId: string): Promise<Character | null> {
    const cached = this.cachedCharacters?.find(
      (character) => character.identifier === characterId,
    );
    if (cached) {
      return cached;
    }

    const snapshot = await get(
      ref(database, `characters/${characterId}`),
    ).catch((error) => {
      throw this.toCoreError(
        error,
        `Failed to read /characters/${characterId}`,
      );
    });
    if (!snapshot.exists()) {
      return null;
    }
    return CharacterConverter.fromFirebase(snapshot.val(), characterId);
  }

  public async loadCharacterLikedCardIds(
    characterId: string,
  ): Promise<string[]> {
    const snapshot = await get(
      ref(database, `characterCards/${characterId}/liked`),
    ).catch((error) => {
      throw this.toCoreError(
        error,
        `Failed to read /characterCards/${characterId}/liked`,
      );
    });
    if (!snapshot.exists()) {
      return [];
    }

    const data = snapshot.val();
    if (!data || typeof data !== "object") {
      return [];
    }
    return Object.keys(data);
  }

  /**
   * Card ids that have a story for this character (the tiny index written next
   * to the liked/disliked sets). Static admin content — cached per character
   * with promise dedupe for the app session.
   */
  public loadCharacterStoryCardIds(characterId: string): Promise<Set<string>> {
    const existing = this.storyCardIdsPromises.get(characterId);
    if (existing) {
      return existing;
    }

    const fetchPromise = (async () => {
      const snapshot = await get(
        ref(database, `characterCards/${characterId}/stories`),
      ).catch((error) => {
        throw this.toCoreError(
          error,
          `Failed to read /characterCards/${characterId}/stories`,
        );
      });
      if (!snapshot.exists()) {
        return new Set<string>();
      }
      const data = snapshot.val();
      if (!data || typeof data !== "object") {
        return new Set<string>();
      }
      return new Set(Object.keys(data));
    })();

    this.storyCardIdsPromises.set(
      characterId,
      fetchPromise.catch((error) => {
        // Do not cache failures — allow a retry on the next call.
        this.storyCardIdsPromises.delete(characterId);
        throw error;
      }),
    );
    return this.storyCardIdsPromises.get(characterId)!;
  }

  /**
   * Loads the match story for a (character, card) pair. Missing stories are
   * cached as null so repeated match checks stay a single read.
   */
  public async loadCharacterStory(
    characterId: string,
    cardId: string,
  ): Promise<CharacterStory | null> {
    const cacheKey = `${characterId}::${cardId}`;
    if (this.cachedStoriesByPair.has(cacheKey)) {
      return this.cachedStoriesByPair.get(cacheKey) ?? null;
    }

    const snapshot = await get(
      ref(
        database,
        `characterStories/${STORIES_LANGUAGE}/${characterId}/${cardId}`,
      ),
    ).catch((error) => {
      throw this.toCoreError(
        error,
        `Failed to read /characterStories/${STORIES_LANGUAGE}/${characterId}/${cardId}`,
      );
    });
    const story = snapshot.exists()
      ? CharacterStoryConverter.fromFirebase(
          snapshot.val(),
          characterId,
          cardId,
        )
      : null;

    this.cachedStoriesByPair.set(cacheKey, story);
    return story;
  }
}
